/**
 * Price Deviation + Warning helpers
 * Used in posting form to warn farmers about unusual prices
 */

// Price deviation thresholds
export const PRICE_THRESHOLDS = {
  OK: 15,           // Within 15% is OK
  SLIGHT: 30,       // 15-30% is slight deviation
  UNUSUAL: 35,      // Above 35% requires manual review
};

/**
 * Calculate price deviation percentage
 * @param {number} askingPrice - The price the user is asking
 * @param {number|null} marketAvg - The market average price
 * @returns {number|null} - Deviation percentage (positive = higher than market)
 */
export function calcDeviationPct(askingPrice, marketAvg) {
  if (!marketAvg || marketAvg <= 0 || !askingPrice) return null;
  return ((askingPrice - marketAvg) / marketAvg) * 100;
}

/**
 * Get the warning key based on deviation percentage
 * @param {number|null} deviationPct - The deviation percentage
 * @returns {string} - Warning key: 'ok', 'slightlyHigh', 'slightlyLow', 'unusual', 'missingMarket'
 */
export function priceWarningKey(deviationPct) {
  if (deviationPct === null) return 'missingMarket';
  
  const absDeviation = Math.abs(deviationPct);
  
  if (absDeviation <= PRICE_THRESHOLDS.OK) return 'ok';
  if (absDeviation <= PRICE_THRESHOLDS.SLIGHT) {
    return deviationPct > 0 ? 'slightlyHigh' : 'slightlyLow';
  }
  return 'unusual';
}

/**
 * Check if price requires manual review
 * @param {number|null} deviationPct - The deviation percentage
 * @returns {boolean}
 */
export function requiresManualReview(deviationPct) {
  if (deviationPct === null) return false;
  return Math.abs(deviationPct) > PRICE_THRESHOLDS.UNUSUAL;
}

/**
 * Get deviation severity class for styling
 * @param {number|null} deviationPct - The deviation percentage
 * @returns {string} - CSS class: 'ok', 'warning', 'danger'
 */
export function getDeviationSeverity(deviationPct) {
  if (deviationPct === null) return 'unknown';
  
  const absDeviation = Math.abs(deviationPct);
  
  if (absDeviation <= PRICE_THRESHOLDS.OK) return 'ok';
  if (absDeviation <= PRICE_THRESHOLDS.UNUSUAL) return 'warning';
  return 'danger';
}

/**
 * Format deviation for display
 * @param {number|null} deviationPct - The deviation percentage
 * @returns {string}
 */
export function formatDeviation(deviationPct) {
  if (deviationPct === null) return '--';
  const sign = deviationPct >= 0 ? '+' : '';
  return `${sign}${deviationPct.toFixed(1)}%`;
}
